/* ─────────────────────────────────────────────────────────────────────────
   "Hi, I'm refillable." refill module

   Markup and first paint come from snippets/product-block-refill-module.liquid;
   this file keeps the module in step with the buy box after that. It listens
   for the theme's `product:variant-change` and re-derives one of three live
   states from the shade the shopper just picked:

     ready      that shade has a refill and it is in stock
     soldout    it has a refill and that refill is sold out
     unmatched  that shade has no refill at all

   The buy box always wins. If a shopper picks a refill shade here and then
   changes the shade above, the module follows the shade above, because that
   is the one they are actually buying.

   Add-to-bag posts to /cart/add.js, which is what Rebuy Smart Cart auto-opens
   off; Smart Cart is only nudged when it is already past initialising, since
   calling show() during 'initializing' just sets pendingShow and can wedge it.
   Never submit natively: layout/theme.liquid redirects /cart to the homepage.
   ───────────────────────────────────────────────────────────────────────── */

(function () {
  'use strict';

  var MODULE_SELECTOR = '[data-refill-module]';

  function fill(template, tokens) {
    return Object.keys(tokens).reduce(function (out, key) {
      return out.split('[' + key + ']').join(tokens[key]);
    }, template || '');
  }

  function setup(root) {
    if (root.dataset.refillReady === 'true') return;
    root.dataset.refillReady = 'true';

    var dataNode = root.querySelector('[data-refill-data]');
    if (!dataNode) return;

    var data;
    try {
      data = JSON.parse(dataNode.textContent);
    } catch (err) {
      return;
    }

    // The empty state has no card to keep in sync, and no variant change can
    // put stock back on the shelf.
    if (root.dataset.state === 'empty') return;

    var strings = data.strings || {};
    var layout = root.dataset.layout;
    var isTabs = layout === 'tabs';

    var els = {
      heading: root.querySelector('[data-refill-heading]'),
      body: root.querySelector('[data-refill-body]'),
      cardTitle: root.querySelector('[data-refill-card-title]'),
      inherited: root.querySelector('[data-refill-inherited-text]'),
      inheritedRow: root.querySelector('[data-refill-inherited]'),
      chip: root.querySelector('[data-refill-chip]'),
      disclosure: root.querySelector('[data-refill-disclosure]'),
      disclosureLabel: root.querySelector('[data-refill-disclosure-label]'),
      picker: root.querySelector('[data-refill-picker]'),
      price: root.querySelector('[data-refill-price]'),
      compareWrap: root.querySelector('[data-refill-compare-wrap]'),
      compare: root.querySelector('[data-refill-compare]'),
      save: root.querySelector('[data-refill-save]'),
      cta: root.querySelector('[data-refill-add]'),
      ctaLabel: root.querySelector('[data-refill-cta-label]'),
      status: root.querySelector('[data-refill-status]')
    };

    var shadeInputs = Array.prototype.slice.call(
      root.querySelectorAll('[data-refill-shades] input[type="radio"]')
    );
    var toneInputs = Array.prototype.slice.call(
      root.querySelectorAll('[data-refill-tones] input[type="radio"]')
    );

    var shadesByName = {};
    (data.shades || []).forEach(function (shade) {
      shadesByName[shade.name] = shade;
    });

    function inputForShade(name) {
      for (var i = 0; i < shadeInputs.length; i++) {
        if (shadeInputs[i].value === name) return shadeInputs[i];
      }
      return null;
    }

    function say(message) {
      if (els.status) els.status.textContent = message;
    }

    /* ── Rendering ─────────────────────────────────────────────────────── */

    function applyPrice(input) {
      if (!input) return;
      if (els.price) els.price.textContent = input.dataset.price || '';
      var save = input.dataset.save;
      var compare = input.dataset.compare;
      if (els.compareWrap) els.compareWrap.hidden = !compare;
      if (els.compare) els.compare.textContent = compare || '';
      if (els.save) {
        els.save.hidden = !save;
        if (save) els.save.textContent = (strings.saveLabel || 'Save') + ' ' + save;
      }
    }

    function applySelection(input) {
      var thumb = root.querySelector('[data-refill-thumb]');

      if (!input) {
        if (els.cardTitle) els.cardTitle.textContent = data.refillTitle;
        // Back to the line shot: a shade-specific photo over a card that names
        // no shade is the kind of small lie that costs trust.
        if (thumb && data.defaultImage) thumb.src = data.defaultImage;
        if (els.cta) {
          els.cta.disabled = true;
          els.cta.dataset.variantId = '';
        }
        if (els.ctaLabel) els.ctaLabel.textContent = strings.ctaLocked || '';
        return;
      }

      if (els.cardTitle) {
        els.cardTitle.textContent = data.refillTitle + ' · ' + input.value;
      }
      if (thumb && input.dataset.image) thumb.src = input.dataset.image;
      applyPrice(input);
      if (els.cta) {
        els.cta.disabled = false;
        els.cta.dataset.variantId = input.dataset.variantId;
      }
      if (els.ctaLabel) {
        els.ctaLabel.textContent = fill(strings.cta, { price: input.dataset.price || '' });
      }
    }

    function showTone(tone) {
      root.querySelectorAll('[data-refill-shades] .refill-module__shade').forEach(function (item) {
        item.hidden = Boolean(tone) && item.dataset.tone !== tone;
      });
    }

    function setPickerOpen(open) {
      if (!els.picker) return;
      els.picker.hidden = !open;
      if (els.disclosure) els.disclosure.setAttribute('aria-expanded', String(open));
      if (els.disclosureLabel) {
        els.disclosureLabel.textContent = open
          ? strings.hideShadesLabel || 'Hide shades'
          : strings.changeShadeLabel || 'Change shade';
      }
    }

    function render(shadeName) {
      var shade = shadesByName[shadeName];
      var state = !shade ? 'unmatched' : shade.available ? 'ready' : 'soldout';

      root.dataset.state = state;
      root.dataset.shade = shadeName || '';

      var tokens = {
        product: data.productTitle,
        vessel: data.vessel,
        shade: shadeName || '',
        count: data.availableCount
      };

      if (els.heading) {
        els.heading.textContent =
          state === 'unmatched'
            ? fill(strings.headingUnmatched, tokens)
            : strings.heading;
      }
      if (els.body) {
        els.body.textContent =
          state === 'unmatched' ? fill(strings.bodyUnmatched, tokens) : fill(strings.body, tokens);
      }

      // The inherited-shade line names a sold-out shade in either layout, and
      // in the tone-tab layout also confirms a shade that is fine. Unmatched is
      // already carried by the heading and body, so the line stands down.
      if (els.inheritedRow) {
        els.inheritedRow.hidden = !(state === 'soldout' || (isTabs && state === 'ready'));
      }
      if (els.chip) {
        els.chip.classList.toggle('refill-module__chip--out', state === 'soldout');
        if (shade && shade.swatch) {
          els.chip.style.backgroundImage = 'url(' + shade.swatch + ')';
        }
      }
      if (els.inherited) {
        if (state === 'soldout') {
          els.inherited.textContent = fill(strings.soldOutNotice, tokens);
        } else {
          var desc = shade && shade.desc ? shade.desc + ' · ' : '';
          els.inherited.textContent = desc + (strings.inheritedSuffix || '');
        }
      }

      if (state === 'ready') {
        var input = inputForShade(shadeName);
        if (input) {
          input.checked = true;
          applySelection(input);
        }
        // The shade is settled, so the grid goes back out of the way -- but the
        // tone still moves with it, so reopening lands on the shopper's own
        // tone rather than wherever the picker was last left.
        if (isTabs) {
          if (shade.tone) selectTone(shade.tone);
          setPickerOpen(false);
        }
      } else {
        shadeInputs.forEach(function (radio) {
          radio.checked = false;
        });
        applySelection(null);
        // Nothing is chosen for the shopper: the picker opens on their own
        // tone so the nearest matches are one glance away, and the CTA stays
        // locked until they actively pick.
        if (isTabs) {
          if (shade && shade.tone) selectTone(shade.tone);
          setPickerOpen(true);
        }
      }
    }

    // Tones whose shades are all sold out have no tab, so a shade can name a
    // tone that is not on offer. Fall to the nearest band that is: the tones
    // run fair to deep, so a neighbour is the closest match, and sending
    // someone in a deep shade to "fair" would be worse than not moving at all.
    function nearestTone(tone) {
      if (!toneInputs.length) return null;
      var offered = toneInputs.map(function (radio) {
        return radio.value;
      });
      if (offered.indexOf(tone) !== -1) return tone;

      var order = data.toneOrder || [];
      var from = order.indexOf(tone);
      if (from === -1) return null;

      var best = null;
      var bestDistance = Infinity;
      order.forEach(function (candidate, index) {
        if (offered.indexOf(candidate) === -1) return;
        var distance = Math.abs(index - from);
        if (distance < bestDistance) {
          bestDistance = distance;
          best = candidate;
        }
      });
      return best;
    }

    function selectTone(tone) {
      var target = nearestTone(tone);
      if (!target) return;
      toneInputs.forEach(function (radio) {
        radio.checked = radio.value === target;
      });
      showTone(target);
    }

    /* ── Wiring ────────────────────────────────────────────────────────── */

    shadeInputs.forEach(function (radio) {
      radio.addEventListener('change', function () {
        if (!radio.checked) return;
        applySelection(radio);
        var shade = shadesByName[radio.value];
        // They have now chosen a shade that works, so the sold-out notice has
        // nothing left to say. The tone-tab layout keeps the line to confirm
        // the new shade; the open row drops it entirely.
        if (els.inheritedRow) els.inheritedRow.hidden = !isTabs;
        if (els.inherited) {
          var desc = shade && shade.desc ? shade.desc + ' · ' : '';
          els.inherited.textContent = desc + (strings.inheritedSuffix || '');
        }
        if (els.chip) {
          els.chip.classList.remove('refill-module__chip--out');
          if (shade && shade.swatch) els.chip.style.backgroundImage = 'url(' + shade.swatch + ')';
        }
      });
    });

    toneInputs.forEach(function (radio) {
      radio.addEventListener('change', function () {
        if (radio.checked) showTone(radio.value);
      });
    });

    if (els.disclosure) {
      els.disclosure.addEventListener('click', function () {
        setPickerOpen(els.picker.hidden);
      });
    }

    document.addEventListener('product:variant-change', function (event) {
      var variant = event.detail && event.detail.variant;
      if (!variant) return;

      var options = variant.options || [variant.option1, variant.option2, variant.option3];
      var matched = null;
      for (var i = 0; i < options.length; i++) {
        if (options[i] && shadesByName[options[i]]) {
          matched = options[i];
          break;
        }
      }
      render(matched || options[0] || '');
    });

    /* ── Add to bag ────────────────────────────────────────────────────── */

    if (els.cta) {
      els.cta.addEventListener('click', function () {
        var variantId = els.cta.dataset.variantId;
        if (!variantId || els.cta.disabled) return;

        var checked = root.querySelector('[data-refill-shades] input:checked');
        var shadeName = checked ? checked.value : '';
        var routeRoot =
          (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

        els.cta.classList.add('is-loading');

        // `_widget` marks the line as this module's sale. Underscore-prefixed,
        // so every cart template here, Smart Cart and checkout all hide it from
        // the shopper; assets/widget-attribution.js reads it back off the cart
        // to set the order's `refill_widget` attribute.
        fetch(routeRoot + 'cart/add.js', {
          method: 'POST',
          credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            items: [{ id: Number(variantId), quantity: 1, properties: { _widget: 'refill-module' } }]
          })
        })
          .then(function (response) {
            if (!response.ok) throw new Error('add failed');
            return response.json();
          })
          .then(function () {
            els.cta.classList.remove('is-loading');
            say(fill(strings.addedStatus, { shade: shadeName }));
            var smartCart = window.Rebuy && window.Rebuy.SmartCart;
            if (smartCart && smartCart.status === 'ready' && typeof smartCart.show === 'function') {
              smartCart.show();
            }
          })
          .catch(function () {
            els.cta.classList.remove('is-loading');
            say(strings.addFailedStatus || '');
          });
      });
    }
  }

  function init() {
    document.querySelectorAll(MODULE_SELECTOR).forEach(setup);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // The theme editor re-renders a section without a page load.
  document.addEventListener('shopify:section:load', init);
})();
